import type {
  TriageGuidanceBlock,
  TriageItem as UiTriageItem,
  TriagePriority,
  TriageSegment,
  TriageStatus,
} from '@elva/ui';
import type {
  TriageDetail,
  TriageGuidance,
  TriageInsuranceCard,
  TriageInsuranceCardOcr,
  TriageListItem,
  TriageNoteRecord,
  TriagePatientInfo,
} from '../api/types';

export interface TriageAdaptResult {
  items: UiTriageItem[];
  uuidById: Map<number, string>;
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso;
  }
}

const warnedStatuses = new Set<string>();

/**
 * Unrecognized statuses (e.g. a new backend value not yet in TriageStatus) must never
 * silently collapse to 'closed' — that hides the item from every default view. Fall back
 * to 'open' so it stays visible, and warn once so the real value gets added here.
 */
function mapStatus(status: TriageListItem['status']): TriageStatus {
  if (status === 'open' || status === 'waiting' || status === 'closed') return status;
  if (!warnedStatuses.has(status)) {
    warnedStatuses.add(status);
    console.warn(`@elva/frontdesk triage: unrecognized status "${status}" from API, treating as 'open'`);
  }
  return 'open';
}

function mapPriority(priority: string): TriagePriority {
  if (priority === 'urgent' || priority === 'high' || priority === 'medium') return priority;
  return 'medium';
}

function mapChannel(channel: string): string {
  const c = channel.toLowerCase();
  if (c === 'sms') return 'SMS';
  if (c === 'email') return 'Email';
  if (c === 'phone' || c === 'call' || c === 'phone call') return 'Phone Call';
  if (c === 'survey') return 'Survey';
  if (c === 'chat' || c === 'website chat') return 'Website Chat';
  return channel;
}

function mapKind(kind: string | undefined): TriageSegment {
  if (kind === 'call_task') return 'call_task';
  return 'attention';
}

function mapSourceLayer(layer: string): string {
  if (layer === 'elva') return 'elva_wide';
  return layer;
}

/**
 * Assignee ids are organization.User integer PKs from `GET /api/triage/assignees`.
 * Anything that isn't a plain integer (e.g. a workspace `p_<n>` provider id) is refused
 * rather than having its digits sent, which would assign an unrelated person.
 * Returns `null` to unassign, a number for a PK, or `undefined` when the id can't be sent.
 * The backend answers 422 `invalid_assignee` for unknown/out-of-scope ids.
 */
export function toApiAssigneeId(id: string | null): number | null | undefined {
  if (id === null) return null;
  return /^\d+$/.test(id) ? Number(id) : undefined;
}

function toUiAssigneeId(id: number | string | null | undefined): string | null {
  return id == null || id === '' ? null : String(id);
}

export function adaptNotes(notes: TriageNoteRecord[] | undefined): UiTriageItem['notes'] {
  return (notes ?? []).map((note) => ({
    author: note.author ?? 'Staff',
    time: note.time ?? (note.created_at ? formatTime(note.created_at) : ''),
    text: note.text,
  }));
}

/**
 * `idByUuid` keeps a uuid → UI id mapping stable across refetches so a selected
 * item doesn't jump to a different one when the list is reordered or grows.
 */
export function adaptTriageItems(
  apiItems: TriageListItem[],
  idByUuid: Map<string, number> = new Map(),
): TriageAdaptResult {
  const uuidById = new Map<number, string>();
  const items: UiTriageItem[] = apiItems.map((item) => {
    let uiId = idByUuid.get(item.id);
    if (uiId === undefined) {
      uiId = idByUuid.size + 1;
      idByUuid.set(item.id, uiId);
    }
    uuidById.set(uiId, item.id);
    const adapted: UiTriageItem = {
      id: uiId,
      patient: item.patient ?? '',
      channel: mapChannel(item.channel),
      priority: mapPriority(item.priority),
      time: item.time || (item.created_at ? formatTime(item.created_at) : ''),
      subject: item.subject,
      status: mapStatus(item.status),
      assigneeId: toUiAssigneeId(item.assigneeId),
      sourceLabel: item.sourceLabel ?? '',
      sourceType: item.sourceType ?? item.channel,
      notes: adaptNotes(item.notes),
      tags: item.tags ?? [],
      itemKind: mapKind(item.itemKind),
    };
    if (item.aiSummary) adapted.aiSummary = item.aiSummary;
    if (item.sentiment) adapted.sentiment = item.sentiment;
    if (item.aiDraftResponse) adapted.aiDraftResponse = item.aiDraftResponse;
    if (item.goalType) adapted.goalType = item.goalType;
    if (item.triggeredBy) adapted.triggeredBy = item.triggeredBy;
    if (item.surfacesIn?.length) adapted.surfacesIn = item.surfacesIn;
    if (item.patientInfo) adapted.patientInfo = adaptPatientInfo(item.patientInfo);
    return adapted;
  });
  return { items, uuidById };
}

function adaptPatientInfo(info: TriagePatientInfo): NonNullable<UiTriageItem['patientInfo']> {
  return { dob: info.dob ?? '', phone: info.phone ?? '', insurance: info.insurance ?? '' };
}

const CARD_FIELD_KEYS = [
  'carrier',
  'plan',
  'memberId',
  'group',
  'subscriberName',
  'relationship',
  'subscriberDob',
] as const;

function pickCardFields(card: TriageInsuranceCard | TriageInsuranceCardOcr) {
  return {
    carrier: card.carrier,
    plan: card.plan,
    memberId: card.memberId,
    group: card.group,
    subscriberName: card.subscriberName,
    relationship: card.relationship,
    subscriberDob: card.subscriberDob,
  };
}

function hasCardValues(card: TriageInsuranceCard | TriageInsuranceCardOcr): boolean {
  return CARD_FIELD_KEYS.some((k) => Boolean(card[k]));
}

/**
 * Card images: `attachments[]` (front then back) wins; otherwise the legacy single
 * `payload.attachment_url` is treated as the front image. Both are fetched with auth
 * unless absolute (older SMS/MMS items carry an external signed URL).
 */
export function adaptSourceImages(detail: TriageDetail): NonNullable<UiTriageItem['sourceImages']> {
  const attachments = detail.attachments ?? [];
  if (attachments.length > 0) {
    const order = { insurance_card_front: 0, insurance_card_back: 1 } as const;
    return [...attachments]
      .sort((a, b) => order[a.kind] - order[b.kind])
      .map((a) => ({
        key: a.id,
        label: a.kind === 'insurance_card_back' ? 'Back' : 'Front',
        url: a.url,
      }));
  }
  const legacy = detail.payload?.attachment_url;
  if (typeof legacy === 'string' && legacy) return [{ key: 'legacy', label: 'Card', url: legacy }];
  return [];
}

/** Detail-only fields that the list rows don't carry, merged onto the selected UI item. */
export function adaptTriageDetail(detail: TriageDetail): Partial<UiTriageItem> {
  const extras: Partial<UiTriageItem> = { notes: adaptNotes(detail.notes) };
  if (detail.patientInfo) extras.patientInfo = adaptPatientInfo(detail.patientInfo);

  const sourceImages = adaptSourceImages(detail);
  if (sourceImages.length > 0) extras.sourceImages = sourceImages;

  const card = detail.insurance_card;
  if (card && hasCardValues(card)) extras.insuranceCard = { ...pickCardFields(card), source: card.source };

  const ocr = detail.insurance_card_ocr;
  if (ocr && hasCardValues(ocr)) extras.insuranceOcr = { ...pickCardFields(ocr), confidence: ocr.confidence };

  return extras;
}

export function adaptTriageGuidance(api: TriageGuidance): TriageGuidanceBlock {
  return {
    taskType: api.task_type,
    routingHint: api.routing_hint,
    items: (api.items ?? []).map((item) => {
      const mapped: TriageGuidanceBlock['items'][number] = {
        sourceLayer: mapSourceLayer(item.source_layer),
        kind: item.kind,
        text: item.text,
      };
      if (item.is_boundary) mapped.isBoundary = true;
      return mapped;
    }),
  };
}
